(function imageZoom(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Modules = global.NewHomeSource.Modules || {};

  if (!global.NewHomeSource.Modules.ImageHandler) {
    return;
  }

  const previousImageZoom = global.NewHomeSource.Modules.ImageZoom;

  const scaleFactor = 0.5;
  const maxZoomLevel = 4;
  const zoomOutClass = 'zoom__canvas--zoomout';

  /**
  * @private
  * clear: clears the touchNow and touchStart properties.
  */
  function clear() {
    if (this.config.touchNow) {
      this.config.touchNow.x = null;
      this.config.touchNow.y = null;
    }

    this.config.touchStart.x = null;
    this.config.touchStart.y = null;
    this.startObject = null;
  }

  /**
   * @private
   * pinchDistance: returns the distance in pixels between two touch points.
   *
   * @param {Touch} t1 first touch point.
   * @param {Touch} t2 second touch point.
   * @returns {number} distance in pixels between the two touch points.
   */
  function pinchDistance(t1, t2) {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  }

  /**
   * @private
   * initOrRebasePinch: captures (or recaptures) the pinch baseline used by subsequent
   * move events: initial distance, current zoom level, the canvas bounding rect and
   * the canvas-relative midpoint of the two touches. Reuses the existing pinch object
   * when present so a rebase preserves identity.
   *
   * @param {Touch} t1 first touch point.
   * @param {Touch} t2 second touch point.
   */
  function initOrRebasePinch(t1, t2) {
    const rect = this.canvas.getBoundingClientRect();
    const pinch = this.pinch || {};
    pinch.startDistance = pinchDistance(t1, t2);
    pinch.startZoom = this.config.zoomLevel;
    pinch.rectLeft = rect.left;
    pinch.rectTop = rect.top;
    pinch.centerX = ((t1.clientX + t2.clientX) / 2) - rect.left;
    pinch.centerY = ((t1.clientY + t2.clientY) / 2) - rect.top;
    this.pinch = pinch;
  }

  /**
   * @private
   * updateZoomOutMode: latches a zoom-out mode that follows the user's traversal of the
   * zoom range. Mode turns ON when the requested level meets or exceeds the max (so the
   * cursor flips to a zoom-out indicator and subsequent clicks keep zooming out) and
   * turns OFF when the level returns to the min. While the level is between 1 and the
   * max the current mode is preserved, so the cursor doesn't flicker mid-traversal.
   *
   * @param {number} requestedLevel the zoom level requested by the caller (pre-clamp).
   */
  function updateZoomOutMode(requestedLevel) {
    if (requestedLevel >= maxZoomLevel) {
      this.zoomOutMode = true;
    } else if (requestedLevel <= 1) {
      this.zoomOutMode = false;
    }

    if (this.zoomOutMode) {
      this.canvas.classList.add(zoomOutClass);
    } else {
      this.canvas.classList.remove(zoomOutClass);
    }
  }

  function constrain() {
    const imageHeight = this.picture.height * this.ratio * this.config.zoomLevel;
    const imageWidth = this.picture.width * this.ratio * this.config.zoomLevel;
    const canvasHeight = this.canvas.height - imageHeight;
    const canvasWidth = this.canvas.width - imageWidth;

    this.bounds.left = Math.min(0, canvasWidth);
    this.bounds.right = Math.max(0, canvasWidth);
    this.bounds.top = Math.min(0, canvasHeight);
    this.bounds.bottom = Math.max(0, canvasHeight);

    this.dx = Math.max(this.bounds.left, Math.min(this.bounds.right, this.dx));
    this.dy = Math.max(this.bounds.top, Math.min(this.bounds.bottom, this.dy));

    if (this.config.zoomLevel === 1) {
      this.dx = (this.config.canvasWidth - this.picture.width * this.ratio * this.config.zoomLevel) / 2;
      this.dy = (this.config.canvasHeight - this.picture.height * this.ratio * this.config.zoomLevel) / 2;
    }
  }

  /**
  * @private
  * isScrolling: returns true if a touchStart event has been triggered, otherwise false.
  */
  function isScrolling() {
    if (!this.startObject) {
      return false;
    }

    return this.startObject.x || this.startObject.x === 0 || this.startObject.y || this.startObject.y === 0;
  }

  /**
  * @private
  * movement: returns the clientX and clientY coordinates of the mouse or touch event.
  *
  * @param {event} event with the information of the mouse or touch event.
  */
  function movement(event) {
    if (event.changedTouches) {
      return {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      };
    }

    return {
      x: event.clientX,
      y: event.clientY,
    };
  }

  /**
  * @private
  * onTouchEnd: handler of mouse up, touch end and pinch end events. When a pinch is
  * in progress and at least two touches remain (e.g. a third finger lifted), the
  * pinch baseline is rebased to the remaining touches so the next move continues
  * smoothly. Otherwise, behaves as a tap-to-zoom for short, low-movement taps.
  *
  * @param {event} event with the information of the mouse or touch event.
  */
  function onTouchEnd(event) {
    if (this.pinch) {
      event.preventDefault();
      if (event.touches && event.touches.length >= 2) {
        initOrRebasePinch.call(this, event.touches[0], event.touches[1]);
        return;
      }
      this.pinch = null;
      clear.call(this);
      return;
    }

    if (!this.startObject || this.startObject.x == null || this.startObject.y == null) {
      clear.call(this);
      return;
    }

    const move = movement(event);
    const totalMovementInX = Math.abs(move.x - this.startObject.x);
    const totalMovementInY = Math.abs(move.y - this.startObject.y);
    const timeElapsed = Date.now() - this.startObject.time;
    const isClick = !(totalMovementInX > 10 || totalMovementInY > 10 || timeElapsed > 1000);

    if (isClick) {
      event.preventDefault();
      let zoomX = event.offsetX;
      let zoomY = event.offsetY;
      if ((zoomX == null || zoomY == null) && event.changedTouches && event.changedTouches[0]) {
        const rect = this.canvas.getBoundingClientRect();
        zoomX = event.changedTouches[0].clientX - rect.left;
        zoomY = event.changedTouches[0].clientY - rect.top;
      }
      this.zoom(!event.shiftKey, zoomX, zoomY);
    }

    clear.call(this);
  }

  /**
  * @private
  * onTouchMove: handler of mouse, touch and pinch move events. When two or more
  * touches are active, scales the image relative to the pinch baseline captured
  * at touch start, anchored to the live midpoint of the touches. Otherwise pans
  * the image (when zoomed in).
  *
  * @param {event} event with the information of the mouse or touch event.
  */
  function onTouchMove(event) {
    if (this.pinch && event.touches && event.touches.length >= 2) {
      event.preventDefault();
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      const newDistance = pinchDistance(t1, t2);
      if (this.pinch.startDistance > 0) {
        const centerX = ((t1.clientX + t2.clientX) / 2) - this.pinch.rectLeft;
        const centerY = ((t1.clientY + t2.clientY) / 2) - this.pinch.rectTop;
        const scale = newDistance / this.pinch.startDistance;
        this.pinch.centerX = centerX;
        this.pinch.centerY = centerY;
        this.setZoomLevel(this.pinch.startZoom * scale, centerX, centerY);
      }
      return;
    }

    if (!this.isScrolling() || this.config.zoomLevel === 1) {
      return;
    }

    event.preventDefault();
    const moveX = this.config.touchNow.x - this.config.touchStart.x;
    const moveY = this.config.touchNow.y - this.config.touchStart.y;

    this.pan(moveX, moveY);
    this.config.touchStart = this.config.touchNow;
    this.config.touchNow = movement(event);
  }

  /**
  * @private
  * onTouchStart: handler of mouse down, touch start and pinch start events. When two
  * or more touches are present, captures the pinch baseline (initial distance, zoom
  * level and canvas-relative midpoint) used by subsequent move events.
  *
  * @param {event} event with the information of the mouse or touch event.
  */
  function onTouchStart(event) {
    if (event.touches && event.touches.length >= 2) {
      event.preventDefault();
      initOrRebasePinch.call(this, event.touches[0], event.touches[1]);
      clear.call(this);
      return;
    }

    const move = movement(event);

    this.config.touchStart = move;
    this.config.touchNow = move;
    this.startObject = {
      x: move.x,
      y: move.y,
      time: Date.now(),
    };
  }

  /**
   * @private
   * onWheel: handler of the mouse wheel event. Wheel direction is taken literally and
   * is intentionally decoupled from the latched zoom-out mode used by click, so a
   * scroll-up always zooms in (until the max) and a scroll-down always zooms out
   * (until the min) regardless of where the click cycle currently sits.
   *
   * @param {event} event with the scroll wheel information.
   */
  function onWheel(event) {
    if (!event.deltaY) {
      return;
    }

    event.preventDefault();

    const step = event.deltaY < 0 ? scaleFactor : -scaleFactor;
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    this.setZoomLevel(this.config.zoomLevel + step, x, y);
  }

  /**
  * @private
  * redraw: draws the picture from scratch in the canvas element.
  */
  function redraw() {
    this.redrawPending = false;
    const dHeight = this.picture.height * this.ratio * this.config.zoomLevel;
    const dWidth = this.picture.width * this.ratio * this.config.zoomLevel;

    constrain.call(this);
    this.drawingContext.clearRect(0, 0, this.config.canvasWidth, this.config.canvasHeight);
    this.drawingContext.drawImage(
      this.picture,
      0,
      0,
      this.picture.width,
      this.picture.height,
      this.dx,
      this.dy,
      dWidth,
      dHeight
    );
  }

  /**
   * @private
   * scheduleRedraw: requests a single redraw on the next animation frame, coalescing
   * multiple calls within the same frame into one. This avoids queueing redundant
   * RAF callbacks during high-frequency gestures such as pinch and pan.
   */
  function scheduleRedraw() {
    if (this.redrawPending) {
      return;
    }
    this.redrawPending = true;
    requestAnimationFrame(this.boundRedraw);
  }

  /**
  * @private
  * onImageLoaded do the maths that needs to know the image dimensions, and attach the events.
  */
  function onImageLoaded() {
    if (this.handlers) {
      this.canvas.removeEventListener('mousedown', this.handlers.touchStart, false);
      this.canvas.removeEventListener('mouseleave', this.handlers.mouseLeave, false);
      this.canvas.removeEventListener('mousemove', this.handlers.touchMove, false);
      this.canvas.removeEventListener('mouseup', this.handlers.touchEnd, false);
      this.canvas.removeEventListener('wheel', this.handlers.wheel, this.handlers.wheelOptions);
      this.canvas.removeEventListener('touchstart', this.handlers.touchStart, false);
      this.canvas.removeEventListener('touchmove', this.handlers.touchMove, false);
      this.canvas.removeEventListener('touchend', this.handlers.touchEnd, false);
    }

    const wheelOptions = { passive: false };
    this.handlers = {
      touchStart: onTouchStart.bind(this),
      mouseLeave: clear.bind(this),
      touchMove: onTouchMove.bind(this),
      touchEnd: onTouchEnd.bind(this),
      wheel: onWheel.bind(this),
      wheelOptions,
    };

    this.canvas.addEventListener('mousedown', this.handlers.touchStart, false);
    this.canvas.addEventListener('mouseleave', this.handlers.mouseLeave, false);
    this.canvas.addEventListener('mousemove', this.handlers.touchMove, false);
    this.canvas.addEventListener('mouseup', this.handlers.touchEnd, false);
    this.canvas.addEventListener('wheel', this.handlers.wheel, this.handlers.wheelOptions);

    this.canvas.addEventListener('touchstart', this.handlers.touchStart, false);
    this.canvas.addEventListener('touchmove', this.handlers.touchMove, false);
    this.canvas.addEventListener('touchend', this.handlers.touchEnd, false);

    this.ratio = Math.min(this.config.canvasWidth / this.picture.width, this.config.canvasHeight / this.picture.height);
    this.config.touchStart = {};
    scheduleRedraw.call(this);
  }

  /**
  * @constructs ImageZoom
  *
  * @param {HTMLCanvasElement} canvas element where the picture is going to be displayed.
  * @param {string} picture URL of the picture to be added to the canvas element.
  */
  function ImageZoom(canvas, picture) {
    const image = new Image();

    this.picture = image;
    this.canvas = canvas;
    this.bounds = {};
    this.bounds.top = Number.NaN;
    this.bounds.left = Number.NaN;
    this.bounds.right = Number.NaN;
    this.bounds.bottom = Number.NaN;
    this.config = {};
    this.config.canvasWidth = canvas.width;
    this.config.canvasHeight = canvas.height;
    this.config.zoomLevel = 1;
    this.zoomOutMode = false;
    this.drawingContext = canvas.getContext('2d');
    this.drawingContext.imageSmoothingEnabled = true;
    this.boundRedraw = redraw.bind(this);
    this.redrawPending = false;

    image.onload = onImageLoaded.bind(this);
    image.src = global.NewHomeSource.Modules.ImageHandler.addJpgParameter(picture);
  }

  /**
  * @public
  * isScrolling: returns true if a touchStart event has been triggered, otherwise false.
  */
  ImageZoom.prototype.isScrolling = isScrolling;

  /**
  * @public
  * pan: moves the image to the given position.
  *
  * @param {number} x coordinate in the X axis to move the image.
  * @param {number} y coordinate in the Y axis to move the image.
  */
  ImageZoom.prototype.pan = function pan(x, y) {
    this.dx += x;
    this.dy += y;
    scheduleRedraw.call(this);
  };

  /**
  * @public
  * redraw: draws the picture from scratch in the canvas element.
  */
  ImageZoom.prototype.redraw = redraw;

  /**
  * @public
  * resize: sets a new width and height to the canvas element.
  *
  * @param {number} width new value for the X axis.
  * @param {number} height new value for the Y axis.
  */
  ImageZoom.prototype.resize = function resize(width, height) {
    this.canvas.width = width * 1;
    this.canvas.height = height * 1;
    this.config.canvasHeight = this.canvas.height;
    this.config.canvasWidth = this.canvas.width;
    this.ratio = Math.min(this.config.canvasWidth / this.picture.width, this.config.canvasHeight / this.picture.height);
    this.dx = (this.config.canvasWidth - this.picture.width * this.ratio * this.config.zoomLevel) / 2;
    this.dy = (this.config.canvasHeight - this.picture.height * this.ratio * this.config.zoomLevel) / 2;
    scheduleRedraw.call(this);
  };

  /**
  * @public
  * setZoomLevel: zooms the image to a specific zoom level, anchoring the image point
  * under (x, y) so it stays fixed on screen during the zoom. Used for continuous
  * gestures such as pinch. When the resulting zoom level is 1, the image is
  * recentered on the canvas and the (x, y) anchor is ignored.
  *
  * @param {number} newLevel desired zoom level (clamped between 1 and the max).
  * @param {number} [x] canvas-relative X coordinate to anchor the zoom around.
  *   Ignored when the resulting zoom level is 1.
  * @param {number} [y] canvas-relative Y coordinate to anchor the zoom around.
  *   Ignored when the resulting zoom level is 1.
  */
  ImageZoom.prototype.setZoomLevel = function setZoomLevel(newLevel, x, y) {
    const clamped = Math.max(1, Math.min(maxZoomLevel, newLevel));
    const previous = this.config.zoomLevel;

    updateZoomOutMode.call(this, newLevel);

    if (clamped === previous) {
      return;
    }

    this.config.zoomLevel = clamped;

    if (clamped === 1) {
      this.dx = (this.config.canvasWidth - this.picture.width * this.ratio * clamped) / 2;
      this.dy = (this.config.canvasHeight - this.picture.height * this.ratio * clamped) / 2;
    } else if (typeof x === 'number' && typeof y === 'number') {
      const scale = clamped / previous;
      this.dx = x - (x - this.dx) * scale;
      this.dy = y - (y - this.dy) * scale;
    }

    constrain.call(this);
    scheduleRedraw.call(this);
  };

  /**
  * @public
  * zoom: increases or decreases the size of the image by one step (scaleFactor),
  * anchored around (x, y). When (x, y) is omitted (e.g. plus/minus button clicks)
  * the anchor defaults to the canvas center so the image's centerpoint stays put.
  * Delegates to setZoomLevel so anchored zoom math, clamping, bounds enforcement and
  * redraw scheduling stay in a single place. While zoom-out mode is latched (after
  * reaching max, until returning to min) every call zooms out regardless of isZoomIn.
  *
  * @param {boolean} isZoomIn true to step up, false to step down. Ignored while
  *   zoom-out mode is latched.
  * @param {number} [x] canvas-relative X coordinate to anchor the zoom around.
  *   Defaults to the canvas horizontal center when omitted.
  * @param {number} [y] canvas-relative Y coordinate to anchor the zoom around.
  *   Defaults to the canvas vertical center when omitted.
  */
  ImageZoom.prototype.zoom = function zoom(isZoomIn, x, y) {
    const direction = (this.zoomOutMode || !isZoomIn) ? -1 : 1;
    const anchorX = typeof x === 'number' ? x : this.config.canvasWidth / 2;
    const anchorY = typeof y === 'number' ? y : this.config.canvasHeight / 2;
    this.setZoomLevel(this.config.zoomLevel + direction * scaleFactor, anchorX, anchorY);
  };

  /**
  * @public
  * zoomIn: explicit one-step zoom-in around the canvas center (or the supplied
  * anchor). Unlike `zoom(true)`, this never reverses direction when zoom-out mode
  * is latched, so a "+" button click at max zoom is a no-op rather than a
  * surprising step-down.
  *
  * @param {number} [x] canvas-relative X anchor. Defaults to canvas horizontal center.
  * @param {number} [y] canvas-relative Y anchor. Defaults to canvas vertical center.
  */
  ImageZoom.prototype.zoomIn = function zoomIn(x, y) {
    const anchorX = typeof x === 'number' ? x : this.config.canvasWidth / 2;
    const anchorY = typeof y === 'number' ? y : this.config.canvasHeight / 2;
    this.setZoomLevel(this.config.zoomLevel + scaleFactor, anchorX, anchorY);
  };

  /**
  * @public
  * zoomOut: explicit one-step zoom-out around the canvas center (or the supplied
  * anchor). Counterpart to zoomIn for "-" button clicks; bypasses the latched
  * zoom-out mode for symmetry.
  *
  * @param {number} [x] canvas-relative X anchor. Defaults to canvas horizontal center.
  * @param {number} [y] canvas-relative Y anchor. Defaults to canvas vertical center.
  */
  ImageZoom.prototype.zoomOut = function zoomOut(x, y) {
    const anchorX = typeof x === 'number' ? x : this.config.canvasWidth / 2;
    const anchorY = typeof y === 'number' ? y : this.config.canvasHeight / 2;
    this.setZoomLevel(this.config.zoomLevel - scaleFactor, anchorX, anchorY);
  };

  /**
   * @public
   *
   * noConflict: Returns control of NewHomeSource.ImageZoom to the previous script.
   */
  ImageZoom.noConflict = function noConflict() {
    global.NewHomeSource.Modules.ImageZoom = previousImageZoom;
    return ImageZoom;
  };

  global.NewHomeSource.Modules.ImageZoom = ImageZoom;
}(window));
